import { LitElement, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { styles } from "./cdn-player.css";
import { hlsTelemetryService } from "./hls-telemetry";
import { SafariPlayer } from "./safari-player";
import { HlsJsPlayer } from "./hlsjs-player";
import { DebugOverlay } from "./debug-overlay";
import type { PlayerState, PlayerInstance, PlayerCallbacks } from "./player-types";

/**
 * Detect Safari browser (not Chrome, Firefox, or Edge on any platform).
 * Safari must use native HLS for AirPlay support.
 */
const isSafari = /^((?!chrome|android|crios|fxios|edgios|edge).)*safari/i.test(navigator.userAgent);

const canPlayNativeHls =
  typeof document !== "undefined" &&
  document.createElement("video").canPlayType("application/vnd.apple.mpegurl") !== "";

// Use native HLS on Safari for AirPlay support, hls.js everywhere else
const useNativeHls = isSafari && canPlayNativeHls;

/**
 * Detect browser and OS from user agent (called once per session).
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = "Other";
  let browserVersion = "";
  let os = "Other";

  // Browser detection (order matters - Edge contains Chrome, etc.)
  if (/Edg\//.test(ua)) {
    browser = "Edge";
    browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] ?? "";
  } else if (/Chrome\//.test(ua)) {
    browser = "Chrome";
    browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] ?? "";
  } else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) {
    browser = "Safari";
    browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] ?? "";
  } else if (/Firefox\//.test(ua)) {
    browser = "Firefox";
    browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] ?? "";
  }

  // OS detection
  if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Linux/.test(ua)) os = "Linux";

  return { browser, browserVersion, os };
};

// Cache browser info (doesn't change during session)
const browserInfo = getBrowserInfo();

/**
 * CDN Player component - renders an HLS live stream player.
 * Works on Safari (native HLS) and other browsers (hls.js).
 * Automatically tracks the live edge.
 */
@customElement("cdn-player")
export class CdnPlayer extends LitElement {
  static override styles = styles;

  @property() streamId?: string;

  @property({ type: Boolean, reflect: true }) autoplay = true;

  @property({ type: Boolean, reflect: true }) muted = true;

  @property({ type: Boolean, reflect: true }) controls = true;

  @property({ type: String, reflect: true, attribute: "aspect-ratio" })
  aspectRatio: "landscape" | "portrait" | "auto" = "auto";

  @state() private detectedAspectRatio: "landscape" | "portrait" = "landscape";

  @state() private playerState: PlayerState = "idle";

  @state() private errorMessage = "";

  @query("video")
  private videoElement?: HTMLVideoElement;

  private player: PlayerInstance | null = null;
  private debugOverlay: DebugOverlay | null = null;
  private telemetrySessionId: string | null = null;
  private streamCheckInterval: number | null = null;
  private telemetryCollectionInterval: number | null = null;
  private telemetryFlushInterval: number | null = null;
  private debugOverlayInterval: number | null = null;

  private get HLS_BASE_URL(): string {
    const isLocalhost = (location.origin ?? "localhost").includes("localhost");
    const isProduction = location.origin.includes("worldofwow.com");
    const streamDomain = isLocalhost
      ? "stream.worldofwow.dev"
      : isProduction
        ? "stream.worldofwow.com"
        : "stream.worldofwow.dev";
    return `https://${streamDomain}/`;
  }
  private readonly STREAM_CHECK_INTERVAL_MS = 2000;
  private readonly TELEMETRY_COLLECTION_INTERVAL_MS = 500;
  private readonly TELEMETRY_FLUSH_INTERVAL_MS = 10000;
  private readonly DEBUG_OVERLAY_INTERVAL_MS = 2000;

  // Telemetry accumulator - aggregates data between flushes
  private telemetryAccumulator = {
    // Previous values for delta calculation
    lastDroppedFrames: 0,
    lastBufferStalls: 0,
    lastFragErrors: 0,
    // Accumulated deltas since last flush
    droppedFramesDelta: 0,
    bufferStallsDelta: 0,
    fragErrorsDelta: 0,
    // Latest values (overwritten each collection)
    currentBitrate: 0,
    bandwidthEstimate: 0,
    currentLevelName: "unknown",
    availableLevels: "",
  };

  // Session accumulator - running totals for the entire viewing session
  private sessionAccumulator = {
    startedAt: 0,
    // For running averages
    bitrateSum: 0,
    bitrateCount: 0,
    // Cumulative totals
    totalDroppedFrames: 0,
    totalBufferStalls: 0,
    totalFragErrors: 0,
    totalQualitySwitches: 0,
    // Track level changes for quality switch counting
    lastLevel: "unknown",
  };

  private get streamUrl(): string {
    return `${this.HLS_BASE_URL}${this.streamId}/master.m3u8`;
  }

  private calculateQualityScore(): "Critical" | "Moderate" | "Good" | "Excellent" {
    const acc = this.sessionAccumulator;
    const durationMinutes = (Date.now() - acc.startedAt) / 60000 || 1;

    const stallsPerMin = acc.totalBufferStalls / durationMinutes;
    const dropsPerMin = acc.totalDroppedFrames / durationMinutes;

    // Buffer length removed from calculation - unreliable on Safari
    if (stallsPerMin > 2 || dropsPerMin > 50) return "Critical";
    if (stallsPerMin > 0.5 || dropsPerMin > 20) return "Moderate";
    if (stallsPerMin > 0.15 || dropsPerMin > 5) return "Good";
    return "Excellent";
  }

  private resetSessionAccumulator(): void {
    this.sessionAccumulator = {
      startedAt: Date.now(),
      bitrateSum: 0,
      bitrateCount: 0,
      totalDroppedFrames: 0,
      totalBufferStalls: 0,
      totalFragErrors: 0,
      totalQualitySwitches: 0,
      lastLevel: "unknown",
    };
  }

  private get shouldShowDebug(): boolean {
    return new URLSearchParams(location.search).get("debug") === "1";
  }

  private get effectiveAspectRatio(): "landscape" | "portrait" {
    return this.aspectRatio === "auto" ? this.detectedAspectRatio : this.aspectRatio;
  }

  override connectedCallback() {
    super.connectedCallback();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.debugOverlay?.remove();
    this.cleanup();
  }

  /**
   * Public cleanup method for explicit cleanup from parent components.
   * Ensures video stops when parent component is disconnected.
   */
  public forceCleanup(): void {
    this.cleanup();
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has("streamId")) {
      const oldStreamId = changedProperties.get("streamId") as string | undefined;
      const newStreamId = this.streamId;

      if (oldStreamId === undefined && newStreamId) {
        // streamId changed from undefined → valid value
        // Initialize player (no cleanup needed - nothing to clean)
        this.initializePlayer();
      } else if (oldStreamId !== undefined && oldStreamId !== newStreamId) {
        // streamId changed from one valid value to another
        // Cleanup old player and reinitialize with new streamId
        this.cleanup();
        this.initializePlayer();
      }
    }

    if (changedProperties.has("playerState") && this.playerState !== "playing") {
      this.debugOverlay?.remove();
    }
  }

  // ==================== Player Initialization ====================

  private initializePlayer(): void {
    if (!this.videoElement || !this.streamId) {
      return;
    }

    if (this.player || this.streamCheckInterval !== null) {
      this.cleanup();
    }

    this.playerState = "loading";

    // Start telemetry collection
    this.telemetrySessionId = crypto.randomUUID();
    this.resetSessionAccumulator();
    hlsTelemetryService.start();

    // Initialize debug overlay
    this.debugOverlay = new DebugOverlay({
      getVideo: () => this.videoElement,
      getLiveEdge: () => (this.player instanceof SafariPlayer ? this.player.getLiveEdge() : null),
      getParentElement: () => this.closest("[popover]") ?? document.body,
    });

    // Create player callbacks
    const callbacks: PlayerCallbacks = {
      onStateChange: (state) => {
        const wasPlaying = this.playerState === "playing";
        this.playerState = state;

        if (state === "playing") {
          this.startTelemetryCollection();
          this.startDebugOverlayUpdates();

          // Notify parent that stream started playing
          if (!wasPlaying) {
            this.dispatchEvent(new CustomEvent("stream-playing", { bubbles: true, composed: true }));
          }
        }
      },
      onError: (message) => {
        this.playerState = "error";
        this.errorMessage = message;
      },
      onAspectRatioDetected: (ratio) => {
        console.log(`[cdn-player] onAspectRatioDetected called with: ${ratio}, current: ${this.detectedAspectRatio}`);
        this.detectedAspectRatio = ratio;
      },
      onStreamEnded: () => {
        this.handleStreamEnded();
      },
      onWaitForStream: () => {
        this.startWaitingForStream();
      },
      getStreamUrl: () => this.streamUrl,
    };

    // Create appropriate player
    if (useNativeHls) {
      this.player = new SafariPlayer(this.videoElement, callbacks);
    } else {
      this.player = new HlsJsPlayer(this.videoElement, callbacks);
    }

    this.player.setup();
  }

  private cleanup(): void {
    this.player?.cleanup();
    this.player = null;

    // Defense-in-depth: ensure video is stopped even if player was null
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = "";
      this.videoElement.load();
    }

    this.stopWaitingForStream();
    this.stopTelemetryCollection();
    this.stopDebugOverlayUpdates();
    this.debugOverlay?.remove();

    // Stop telemetry collection and flush remaining data
    hlsTelemetryService.stop();
    this.telemetrySessionId = null;

    this.playerState = "idle";
  }

  // ==================== Stream Availability ====================

  private async checkStreamAvailability(): Promise<boolean> {
    try {
      const response = await fetch(this.streamUrl, { method: "HEAD" });
      return response.ok;
    } catch {
      return false;
    }
  }

  private startWaitingForStream(): void {
    const wasPlaying = this.playerState === "playing";
    this.playerState = "waiting";

    // Notify parent that stream stopped playing (so polling can resume)
    if (wasPlaying) {
      this.dispatchEvent(new CustomEvent("stream-ended", { bubbles: true, composed: true }));
    }

    this.stopWaitingForStream();

    this.streamCheckInterval = window.setInterval(async () => {
      const isAvailable = await this.checkStreamAvailability();

      // Guard: another callback may have already initialized the player
      if (this.streamCheckInterval === null || this.playerState !== "waiting") {
        return;
      }

      if (isAvailable) {
        this.stopWaitingForStream();
        this.initializePlayer();
      }
    }, this.STREAM_CHECK_INTERVAL_MS);
  }

  private stopWaitingForStream(): void {
    if (this.streamCheckInterval !== null) {
      clearInterval(this.streamCheckInterval);
      this.streamCheckInterval = null;
    }
  }

  private handleStreamEnded(): void {
    // Don't handle ended event if autoplay was blocked
    if (this.playerState === "autoplay-blocked") {
      return;
    }

    // Clean up player but keep video visible with last frame
    this.player?.cleanup();
    this.player = null;

    // Notify parent that stream ended (for polling control)
    this.dispatchEvent(new CustomEvent("stream-ended", { bubbles: true, composed: true }));

    // Start background polling for new live stream
    this.startStreamReconnectPolling();
  }

  private startStreamReconnectPolling(): void {
    if (this.streamCheckInterval !== null) return;

    this.streamCheckInterval = window.setInterval(async () => {
      try {
        const response = await fetch(this.streamUrl, { cache: "no-store" });

        // Guard: another callback may have already initialized the player
        if (this.streamCheckInterval === null) {
          return;
        }

        if (response.ok) {
          const manifest = await response.text();

          // Guard again after second await
          if (this.streamCheckInterval === null) {
            return;
          }

          // Only reinitialize if it's a NEW live stream (no EXT-X-ENDLIST)
          if (!manifest.includes("#EXT-X-ENDLIST")) {
            this.stopWaitingForStream();
            this.initializePlayer();
          }
        }
      } catch {
        // Network error - keep polling
      }
    }, this.STREAM_CHECK_INTERVAL_MS);
  }

  // ==================== Telemetry Collection ====================

  private startTelemetryCollection(): void {
    if (this.telemetryCollectionInterval !== null) return;

    // Collect telemetry every 500ms for debug overlay responsiveness
    this.telemetryCollectionInterval = window.setInterval(() => {
      this.collectTelemetry();
    }, this.TELEMETRY_COLLECTION_INTERVAL_MS);

    // Flush aggregated telemetry every 6s (1 row per flush instead of 12)
    this.telemetryFlushInterval = window.setInterval(() => {
      this.flushTelemetry();
    }, this.TELEMETRY_FLUSH_INTERVAL_MS);

    // Initial collection
    this.collectTelemetry();
  }

  private stopTelemetryCollection(): void {
    if (this.telemetryCollectionInterval !== null) {
      clearInterval(this.telemetryCollectionInterval);
      this.telemetryCollectionInterval = null;
    }
    if (this.telemetryFlushInterval !== null) {
      clearInterval(this.telemetryFlushInterval);
      this.telemetryFlushInterval = null;
    }
    // Final flush before stopping
    this.flushTelemetry();
  }

  private collectTelemetry(): void {
    if (!this.player) return;

    const stats = this.player.getDebugStats();
    const { droppedFrames, bandwidthEstimate, bufferStalledErrors, fragLoadErrors, currentLevel, levels } = stats;

    // Calculate and accumulate deltas
    const droppedDelta = Math.max(0, droppedFrames - this.telemetryAccumulator.lastDroppedFrames);
    const stallsDelta = Math.max(0, bufferStalledErrors - this.telemetryAccumulator.lastBufferStalls);
    const fragDelta = Math.max(0, fragLoadErrors - this.telemetryAccumulator.lastFragErrors);

    this.telemetryAccumulator.droppedFramesDelta += droppedDelta;
    this.telemetryAccumulator.bufferStallsDelta += stallsDelta;
    this.telemetryAccumulator.fragErrorsDelta += fragDelta;

    // Update last values for next delta calculation
    this.telemetryAccumulator.lastDroppedFrames = droppedFrames;
    this.telemetryAccumulator.lastBufferStalls = bufferStalledErrors;
    this.telemetryAccumulator.lastFragErrors = fragLoadErrors;

    // Store latest snapshot values
    const levelInfo = currentLevel >= 0 && levels[currentLevel] ? levels[currentLevel] : null;
    const currentLevelName = levelInfo
      ? levelInfo.height > 0
        ? `${levelInfo.height}p`
        : `${Math.round(levelInfo.bitrate / 1000)}Kbps`
      : "unknown";
    const currentBitrate = levelInfo ? Math.round(levelInfo.bitrate / 1000) : 0;

    this.telemetryAccumulator.currentLevelName = currentLevelName;
    this.telemetryAccumulator.currentBitrate = currentBitrate;
    this.telemetryAccumulator.bandwidthEstimate = bandwidthEstimate;
    this.telemetryAccumulator.availableLevels = levels
      .map((l) => (l.height > 0 ? `${l.height}p` : `${Math.round(l.bitrate / 1000)}Kbps`))
      .join("|");

    // Update session accumulators for running totals
    this.sessionAccumulator.totalDroppedFrames += droppedDelta;
    this.sessionAccumulator.totalBufferStalls += stallsDelta;
    this.sessionAccumulator.totalFragErrors += fragDelta;

    // Track quality switches
    if (currentLevelName !== "unknown" && this.sessionAccumulator.lastLevel !== "unknown") {
      if (currentLevelName !== this.sessionAccumulator.lastLevel) {
        this.sessionAccumulator.totalQualitySwitches++;
      }
    }
    this.sessionAccumulator.lastLevel = currentLevelName;

    // Update running averages
    if (currentBitrate > 0) {
      this.sessionAccumulator.bitrateSum += currentBitrate;
      this.sessionAccumulator.bitrateCount++;
    }
  }

  // ==================== Debug Overlay ====================

  private startDebugOverlayUpdates(): void {
    if (this.debugOverlayInterval !== null) return;

    this.debugOverlayInterval = window.setInterval(() => {
      this.updateDebugOverlay();
    }, this.DEBUG_OVERLAY_INTERVAL_MS);

    // Initial update
    this.updateDebugOverlay();
  }

  private stopDebugOverlayUpdates(): void {
    if (this.debugOverlayInterval !== null) {
      clearInterval(this.debugOverlayInterval);
      this.debugOverlayInterval = null;
    }
  }

  private updateDebugOverlay(): void {
    if (!this.player || !this.shouldShowDebug || this.playerState !== "playing") return;

    const stats = this.player.getDebugStats();
    this.debugOverlay?.render(stats, useNativeHls);
  }

  /**
   * Flush aggregated telemetry to server (called every 10s).
   * Sends ONE snapshot with pre-computed values, then resets delta accumulators.
   */
  private flushTelemetry(): void {
    if (!this.telemetrySessionId || !this.streamId) return;

    const sessionDurationSeconds = Math.round((Date.now() - this.sessionAccumulator.startedAt) / 1000);
    const avgBitrate =
      this.sessionAccumulator.bitrateCount > 0
        ? Math.round(this.sessionAccumulator.bitrateSum / this.sessionAccumulator.bitrateCount)
        : 0;

    // Send v2 snapshot with pre-computed values
    hlsTelemetryService.recordV2({
      session_id: this.telemetrySessionId,
      stream_id: this.streamId,
      timestamp: Date.now(),
      // Current snapshot
      current_level: this.telemetryAccumulator.currentLevelName,
      current_bitrate: this.telemetryAccumulator.currentBitrate,
      bandwidth_estimate: this.telemetryAccumulator.bandwidthEstimate,
      // Deltas for this window
      dropped_frames_delta: this.telemetryAccumulator.droppedFramesDelta,
      buffer_stalls_delta: this.telemetryAccumulator.bufferStallsDelta,
      frag_errors_delta: this.telemetryAccumulator.fragErrorsDelta,
      // Session running totals
      session_duration_seconds: sessionDurationSeconds,
      avg_bitrate: avgBitrate,
      total_dropped_frames: this.sessionAccumulator.totalDroppedFrames,
      total_buffer_stalls: this.sessionAccumulator.totalBufferStalls,
      total_frag_errors: this.sessionAccumulator.totalFragErrors,
      total_quality_switches: this.sessionAccumulator.totalQualitySwitches,
      // Pre-computed quality score
      quality_score: this.calculateQualityScore(),
      // Browser info
      browser: browserInfo.browser,
      browser_version: browserInfo.browserVersion,
      operating_system: browserInfo.os,
    });

    // Reset delta accumulators for next period
    this.telemetryAccumulator.droppedFramesDelta = 0;
    this.telemetryAccumulator.bufferStallsDelta = 0;
    this.telemetryAccumulator.fragErrorsDelta = 0;
  }

  // ==================== Event Handlers ====================

  private handleRetry = (): void => {
    this.cleanup();
    this.initializePlayer();
  };

  private handlePlayClick = (): void => {
    const video = this.videoElement;
    if (video) {
      // If video ended while waiting for user interaction, seek to live position
      if (video.ended && this.player instanceof HlsJsPlayer) {
        const hls = this.player.getHlsInstance();
        if (hls?.liveSyncPosition) {
          video.currentTime = hls.liveSyncPosition;
        }
      }
      void video.play();
      this.playerState = "playing";
    }
  };

  // ==================== Rendering ====================

  private renderLoadingState() {
    return html`
      <div class="loading-state">
        <div class="spinner"></div>
        <div>Loading stream...</div>
      </div>
    `;
  }

  private renderWaitingState() {
    return html`
      <div class="waiting-state">
        <div class="spinner"></div>
        <div>Waiting for streamer...</div>
      </div>
    `;
  }

  private renderAutoplayBlockedState() {
    return html`
      <div class="autoplay-blocked-state" @click=${this.handlePlayClick}>
        <div class="play-icon">▶</div>
        <div>Click to play</div>
      </div>
    `;
  }

  private renderErrorState() {
    return html`
      <div class="error-state">
        <div class="error-message">${this.errorMessage}</div>
        <button class="retry-button" @click=${this.handleRetry}>Retry</button>
      </div>
    `;
  }

  override render() {
    const wrapperClasses = {
      "video-wrapper": true,
      portrait: this.effectiveAspectRatio === "portrait",
    };

    return html`
      <div class="player-container">
        <div class=${classMap(wrapperClasses)}>
          <video
            ?autoplay=${this.autoplay}
            ?muted=${this.muted}
            ?controls=${this.controls}
            playsinline
            webkit-playsinline
            x-webkit-airplay="allow"
          ></video>
          ${this.playerState === "loading" ? this.renderLoadingState() : nothing}
          ${this.playerState === "waiting" ? this.renderWaitingState() : nothing}
          ${this.playerState === "autoplay-blocked" ? this.renderAutoplayBlockedState() : nothing}
          ${this.playerState === "error" ? this.renderErrorState() : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cdn-player": CdnPlayer;
  }
}
